import {
  FiActivity,
  FiArchive,
  FiBarChart2,
  FiBell,
  FiBookOpen,
  FiBox,
  FiFileText,
  FiGift,
  FiGrid,
  FiHelpCircle,
  FiHome,
  FiLayers,
  FiMail,
  FiMessageCircle,
  FiMessageSquare,
  FiPercent,
  FiSearch,
  FiSettings,
  FiShoppingBag,
  FiSmartphone,
  FiStar,
  FiTag,
  FiTruck,
  FiUsers,
} from "react-icons/fi";

const item = (link, text, icon, permission, options = {}) => ({
  link,
  text,
  icon,
  permission,
  ...options,
});

export const ADMIN_NAV_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    description: "Business snapshot",
    icon: FiHome,
    items: [
      item("/admin", "Dashboard", FiGrid),
      item("/data-export", "Data export", FiArchive),
    ],
  },
  {
    id: "commerce",
    label: "Commerce",
    description: "Orders and revenue",
    icon: FiShoppingBag,
    items: [
      item("/AllOrders/list", "All orders", FiShoppingBag, "R_ORDERS"),
      item("/invoices/list", "Invoices", FiFileText, "R_ORDERS"),
      item("/coupon/list", "Coupons", FiPercent, "R_COUPONS"),
      item("/reviews", "Product reviews", FiStar, "R_TICKETS"),
    ],
  },
  {
    id: "customers",
    label: "Customers",
    description: "People and support",
    icon: FiUsers,
    items: [
      item("/customerlist", "Customers", FiUsers, "R_CUSTOMERS"),
      item("/support-cases", "Support cases", FiHelpCircle, "R_TICKETS"),
      item("/enquiry", "Enquiries", FiMessageSquare, "R_ENQUIRIES"),
      item("/mails", "Email centre", FiMail, "W_MAILS"),
      item("/email-tester", "Email tester", FiActivity, "W_MAILS"),
    ],
  },
  {
    id: "content",
    label: "Content",
    description: "Website and catalogue",
    icon: FiLayers,
    items: [
      item("/pages", "Website pages", FiFileText, "R_PAGES"),
      item("/collection/list", "Collections", FiLayers, "R_COLLECTIONS"),
      item("/tags", "Product tags", FiTag, "W_TAGS"),
      item("/category", "Categories", FiGrid, "W_CATEGORIES"),
      item("/brands", "Brands", FiBox, "R_BRANDS"),
      item("/blog", "Blog posts", FiBookOpen),
      item("/blog/comments", "Blog comments", FiMessageCircle),
    ],
  },
  {
    id: "app",
    label: "Mobile app",
    description: "App-only workspace",
    icon: FiSmartphone,
    appOnly: true,
    items: [
      item("/app-pages", "App pages", FiSmartphone, "R_PAGES"),
      item("/app-pogi-insights", "Pogi insights", FiActivity),
      item("/app-pogi-v2-conversation", "App Pogi v2 sessions", FiMessageSquare),
      item("/pogi-points", "Pogi Points", FiGift, "R_COUPONS"),
      item("/push-notifications", "Push notifications", FiBell),
    ],
  },
  {
    id: "merchants",
    label: "Merchants",
    description: "Partners and products",
    icon: FiTruck,
    merchantWorkspace: true,
    items: [],
  },
  {
    id: "growth",
    label: "Growth",
    description: "Search and conversations",
    icon: FiBarChart2,
    items: [
      item("/exports", "Product SEO export", FiSearch, "R_SEO"),
      item("/pogi-conversation", "Website Pogi sessions", FiMessageCircle),
    ],
  },
  {
    id: "settings",
    label: "Settings",
    description: "Dashboard configuration",
    icon: FiSettings,
    items: [
      item("/admin/settings", "Settings", FiSettings),
      item("/admin/settings?page=config", "System configuration", FiGrid),
    ],
  },
];

export const MERCHANT_NAV_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    description: "Business snapshot",
    icon: FiHome,
    items: [item("/", "Dashboard", FiGrid)],
  },
  {
    id: "commerce",
    label: "Commerce",
    description: "Products and orders",
    icon: FiShoppingBag,
    items: [
      item("/products", "Your products", FiBox, undefined, { appendMerchantQuery: true }),
      item("/orders", "Your orders", FiShoppingBag),
      item("/payout", "Payouts", FiFileText),
    ],
  },
  {
    id: "workspace",
    label: "Workspace",
    description: "Planning tools",
    icon: FiLayers,
    items: [item("/todo", "To-do list", FiActivity)],
  },
  {
    id: "settings",
    label: "Settings",
    description: "Account preferences",
    icon: FiSettings,
    items: [
      item("/merchant/settings", "Settings", FiSettings),
      item("/profile", "Profile", FiUsers),
    ],
  },
];

export const SUPER_ADMIN_NAV_SECTIONS = [
  {
    id: "overview",
    label: "Super admin",
    description: "Platform controls",
    icon: FiGrid,
    items: [
      item("/dashboard", "Dashboard", FiGrid),
      item("/super/merchants", "Merchants", FiTruck),
      item("/super/admins", "Administrators", FiUsers),
      item("/super/logs", "System logs", FiActivity),
    ],
  },
];

const ADMIN_PATH_GROUPS = [
  { id: "app", prefixes: ["/app-page", "/app-pages", "/app-pogi", "/pogi-points", "/push-notifications"] },
  { id: "settings", prefixes: ["/admin/settings"] },
  { id: "merchants", prefixes: ["/merchant", "/createmerchant", "/payouts", "/admin/order"] },
  { id: "customers", prefixes: ["/customer", "/support-cases", "/enquiry", "/mails", "/email-tester"] },
  { id: "commerce", prefixes: ["/AllOrders", "/invoices", "/coupon", "/reviews", "/review"] },
  { id: "content", prefixes: ["/pages", "/page/", "/collection", "/tags", "/tag", "/category", "/brands", "/blog"] },
  { id: "growth", prefixes: ["/exports", "/pogi-conversation"] },
];

export const sectionIdForPath = (pathname = "/admin", role = "ROLE_ADMIN") => {
  if (role === "ROLE_MERCHANT") {
    if (["/products", "/orders", "/order", "/payout"].some((prefix) => pathname.startsWith(prefix))) return "commerce";
    if (pathname.startsWith("/todo")) return "workspace";
    if (["/merchant/settings", "/profile"].some((prefix) => pathname.startsWith(prefix))) return "settings";
    return "overview";
  }

  if (role === "ROLE_SUPER_ADMIN") return "overview";
  return ADMIN_PATH_GROUPS.find((group) => group.prefixes.some((prefix) => pathname.startsWith(prefix)))?.id || "overview";
};

export const sectionsForRole = (role) => {
  if (role === "ROLE_MERCHANT") return MERCHANT_NAV_SECTIONS;
  if (role === "ROLE_SUPER_ADMIN") return SUPER_ADMIN_NAV_SECTIONS;
  return ADMIN_NAV_SECTIONS;
};

const plainPath = (link = "") => link.split("?")[0];

export const activeItemForPath = (sections, pathname) => {
  const items = sections.flatMap((section) => section.items || []);
  return items.find((entry) => plainPath(entry.link) === pathname)
    || [...items]
      .sort((left, right) => plainPath(right.link).length - plainPath(left.link).length)
      .find((entry) => plainPath(entry.link) !== "/" && pathname.startsWith(`${plainPath(entry.link)}/`))
    || null;
};
